import React from 'react';

import { Box, Divider, Grid, Skeleton, Typography } from '@mui/material';

import { ShowFnOutput, useModal } from 'mui-modal-provider';

import Button from '../../../components/Button/Button';
import ProfileUpdateDialog, {
  ProfileUpdateDialogProps,
} from '../../../components/Dialogs/ProfileUpdate/ProfileUpdateDialog';
import InstagramTag from '../../../components/InstagramTag/InstagramTag';
import TikTokTag from '../../../components/TikTokTag/TikTokTag';
import { Venue } from '../../../graphql/API';
import { useAppSelector } from '../../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import DetailRow from '../../OfferDetail/components/DetailRow';

const getAddressString = (profile?: Venue) => {
  if (!profile) {
    return '';
  }
  return [
    profile.address1,
    profile.address2,
    profile.city,
    profile.postalCode + ' ' + profile.stateProvince,
    profile.country,
  ]
    .filter((i) => !!i)
    .join(', ');
};

const ProfilePanel: React.FC = () => {
  const profileUpdateModalRef = React.useRef<ShowFnOutput<ProfileUpdateDialogProps>>();
  const { showModal } = useModal();
  const venueState = useAppSelector((state) => state.venue.value);

  if (!venueState) {
    return <Skeleton variant='rounded' width='100%' height={320} />;
  }

  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online);

  const websiteHref = venueState.website
    ? venueState.website.startsWith('http://') || venueState.website.startsWith('https://')
      ? venueState.website
      : `https://${venueState.website}`
    : '';

  const contactName = `${venueState.contactFirstname ?? ''} ${
    venueState.contactLastname ?? ''
  }`.trim();

  return (
    <Box
      sx={{
        backgroundColor: '#FCFCFC',
        border: '1px solid #f1f2f1',
        borderRadius: '14px',
        p: 3,
      }}
    >
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={1.5}>
        <Typography
          sx={{
            margin: 0,
            color: '#111',
            fontWeight: 700,
            fontFamily: 'Poppins',
            fontSize: '13px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Profile Info
        </Typography>

        <Button
          id='btn-edit'
          variant='outlined'
          size='small'
          color='primary'
          sx={{ px: 3 }}
          onClick={() =>
            (profileUpdateModalRef.current = showModal(ProfileUpdateDialog, {
              title: 'Edit info',
              modalRef: profileUpdateModalRef,
            }))
          }
        >
          Edit info
        </Button>
      </Box>

      <Grid container columnSpacing={4}>
        <Grid item xs={12} sm={6}>
          <DetailRow label='Name' description={venueState.name} />
          <DetailRow
            label={isOnlineBrand ? 'Country' : 'Address'}
            description={isOnlineBrand ? venueState?.country : getAddressString(venueState)}
          />
          <DetailRow label='Phone Number' description={venueState.phone} />
          <DetailRow
            label='Instagram'
            description={
              venueState?.instagramHandle ? (
                <InstagramTag
                  username={venueState.instagramHandle}
                  textColor='#1E55A1'
                  customFontSize='13px'
                />
              ) : null
            }
          />
          <DetailRow
            label='TikTok'
            description={
              venueState?.tiktokHandle ? (
                <TikTokTag
                  tiktokHandle={venueState.tiktokHandle}
                  textColor='#1E55A1'
                  customFontSize='13px'
                />
              ) : (
                '—'
              )
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DetailRow label='Contact name' description={contactName} />
          <DetailRow label='Position' description={venueState.contactPosition} />
          <DetailRow label='Email' description={venueState.email} />
          <DetailRow
            label='Website'
            description={
              venueState.website ? (
                <a
                  target='_blank'
                  rel='noopener noreferrer'
                  href={websiteHref}
                  style={{ color: '#1E55A1', fontSize: '13px', fontFamily: 'Poppins' }}
                >
                  {venueState.website}
                </a>
              ) : null
            }
          />
        </Grid>
      </Grid>

      <Box mt={3}>
        <Divider sx={{ mb: 2, borderColor: '#E6E6E6' }} />
        <Typography
          sx={{
            margin: 0,
            color: '#848484',
            fontWeight: 400,
            fontFamily: 'Poppins',
            fontSize: '13px',
            mb: 1,
          }}
        >
          Description
        </Typography>
        <Typography
          sx={{
            color: venueState.description ? '#111' : '#848484',
            fontSize: '13px',
            fontWeight: 400,
            fontFamily: 'Poppins',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
          }}
        >
          {venueState.description || '—'}
        </Typography>
      </Box>
    </Box>
  );
};

export default ProfilePanel;
